import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { magic, validateMagicConfiguration } from '../lib/magic';
import LoadingSpinner from './LoadingSpinner';

const OAuthCallback: React.FC = () => {
  const { user, isLoading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent multiple executions
      if (hasProcessed) {
        console.log('OAuthCallback - Already processed, skipping...');
        return;
      }
      
      try {
        console.log('OAuthCallback - Processing OAuth result...');
        console.log('OAuthCallback - current URL:', window.location.href);
        setHasProcessed(true);
        
        // First, validate Magic configuration
        const configValidation = await validateMagicConfiguration();
        if (!configValidation.isValid) {
          console.error('OAuthCallback - Magic configuration issues:', configValidation.errors);
          setError(`Configuration error: ${configValidation.errors.join(', ')}`);
          setIsProcessing(false);
          return;
        }
        
        // Check if we're in a valid OAuth callback state
        const urlParams = new URLSearchParams(window.location.search);
        const hasOAuthParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('error');
        
        console.log('OAuthCallback - URL params:', {
          code: urlParams.has('code'),
          state: urlParams.has('state'),
          error: urlParams.has('error'),
          hasOAuthParams
        });
        
        // Check if user is already logged in (handles case where OAuth was already processed)
        let isLoggedIn = false;
        try {
          isLoggedIn = await magic.user.isLoggedIn();
          console.log('OAuthCallback - User login status:', isLoggedIn);
        } catch (loginError) {
          console.warn('OAuthCallback - Could not check login status:', loginError);
          // Continue with the flow even if we can't check login status
        }
        
        if (!hasOAuthParams && !isLoggedIn) {
          console.log('OAuthCallback - No OAuth parameters found in URL and user not logged in');
          setError('Invalid OAuth callback - no authentication parameters found');
          setIsProcessing(false);
          return;
        }
        
        // The AuthContext will handle getting the OAuth redirect result and setting the user
        // We just need to wait for it to complete
        if (isLoggedIn) {
          console.log('OAuthCallback - User already logged in, waiting for AuthContext to process...');
        } else {
          console.log('OAuthCallback - OAuth parameters found, waiting for AuthContext to process...');
        }
        setIsProcessing(false);
      } catch (error) {
        console.error('OAuthCallback - Error processing OAuth:', error);
        
        // Provide more specific error messages based on the error type
        let errorMessage = 'Authentication failed';
        
        if (error instanceof Error) {
          if (error.message.includes('[-32600]')) {
            errorMessage = 'OAuth credentials verification failed. Please check your Magic Link dashboard configuration for Google OAuth settings.';
          } else if (error.message.includes('RPC Error')) {
            errorMessage = 'Magic Link service error. Please check your API key and try again.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Authentication timed out. Please try again.';
          } else if (error.message.includes('network')) {
            errorMessage = 'Network error. Please check your internet connection and try again.';
          } else {
            errorMessage = `Authentication failed: ${error.message}`;
          }
        }
        
        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [hasProcessed]);

  useEffect(() => {
    console.log('OAuthCallback - user:', user);
    console.log('OAuthCallback - isLoading:', isLoading);
    console.log('OAuthCallback - current URL:', window.location.href);
    
    // Redirect after successful authentication
    if (user && !isLoading) {
      const authContext = sessionStorage.getItem('authContext');
      const redirectUrl = sessionStorage.getItem('postAuthRedirectUrl') || '/';
      const pendingParlayData = sessionStorage.getItem('pendingParlayData');
      
      console.log('OAuthCallback: Auth context:', authContext);
      console.log('OAuthCallback: Redirecting to:', redirectUrl);
      console.log('OAuthCallback: Has pending parlay data:', !!pendingParlayData);
      
      // Only clear auth context and redirect URL, keep parlay data for App.tsx to process
      sessionStorage.removeItem('postAuthRedirectUrl');
      
      window.location.href = redirectUrl;
    }
  }, [user, isLoading]);

  if (isLoading || isProcessing) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          padding: '20px',
          width: '100%'
        }}>
          <h3 style={{ color: '#DC2626', margin: '0 0 12px 0' }}>Authentication Error</h3>
          <p style={{ color: '#DC2626', margin: '0 0 16px 0' }}>{error}</p>

          {error.includes('OAuth credentials verification failed') && (
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '6px',
              padding: '12px',
              margin: '12px 0',
              textAlign: 'left'
            }}>
              <h4 style={{ color: '#92400E', margin: '0 0 8px 0', fontSize: '14px' }}>Troubleshooting Steps:</h4>
              <ul style={{ color: '#92400E', margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
                <li>Check your Magic Link dashboard OAuth configuration</li>
                <li>Verify Google OAuth credentials are correctly set up</li>
                <li>Ensure redirect URIs match your application URLs</li>
                <li>Confirm your Magic API key is valid and has OAuth permissions</li>
              </ul>
            </div>
          )}

          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF3100',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // If we're not loading, not processing, and have no error, but still no user,
  // it means AuthContext is handling it - just show loading state
  return <LoadingSpinner />;
};

export default OAuthCallback;